import { useMemo, useState } from "react";
import { useGetList, useRecordContext } from "ra-core";
import {
  Mail,
  Send,
  Phone,
  StickyNote,
  Calendar,
  Plus,
  ChevronDown,
  Inbox,
  CheckSquare,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import { AudioRecorderDialog } from "../recordings/AudioRecorderDialog";
import type {
  Appointment,
  Contact,
  ContactNote,
  ContactRecording,
  EmailMessage,
} from "../types";

type ItemType = "note" | "email_in" | "email_out" | "appel" | "rdv";

type FilterKey = "tous" | "mails" | "notes" | "appels" | "rdv";

type TimelineItem = {
  key: string;
  type: ItemType;
  date: string;
  title?: string;
  subject?: string;
  body?: string;
  from?: string;
  to?: string;
  duration?: string;
};

const FILTERS: { key: FilterKey; label: string; matches: ItemType[] }[] = [
  {
    key: "tous",
    label: "Tous",
    matches: ["note", "email_in", "email_out", "appel", "rdv"],
  },
  { key: "mails", label: "Mails", matches: ["email_in", "email_out"] },
  { key: "notes", label: "Notes", matches: ["note"] },
  { key: "appels", label: "Appels", matches: ["appel"] },
  { key: "rdv", label: "RDV", matches: ["rdv"] },
];

const ICONS: Record<ItemType, typeof Mail> = {
  note: StickyNote,
  email_in: Mail,
  email_out: Send,
  appel: Phone,
  rdv: Calendar,
};

const ICON_BG: Record<ItemType, string> = {
  note: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  email_in: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  email_out: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  appel: "bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300",
  rdv: "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300",
};

const TYPE_LABEL: Record<ItemType, string> = {
  note: "Note",
  email_in: "Email reçu",
  email_out: "Email envoyé",
  appel: "Appel téléphonique",
  rdv: "Rendez-vous",
};

function relativeDate(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr });
  } catch {
    return "";
  }
}

function absoluteDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function durationLabel(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}min ${String(s).padStart(2, "0")}s`;
}

function snippet(text: string | null | undefined, max = 220): string {
  if (!text) return "";
  const stripped = text.replace(/\s+/g, " ").trim();
  return stripped.length > max ? stripped.slice(0, max) + "…" : stripped;
}

/**
 * Timeline agrégée d'un contact (notes + emails + appels + RDV).
 * Sprint 2 · T2.4. Cf design/atomic-crm/timeline.jsx.
 */
export function ContactTimeline() {
  const record = useRecordContext<Contact>();
  const [filter, setFilter] = useState<FilterKey>("tous");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [noteOpen, setNoteOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);

  const enabled = !!record?.id;
  const filterParams = { contact_id: record?.id };

  const notesQ = useGetList<ContactNote>(
    "contact_notes",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "date", order: "DESC" },
      filter: filterParams,
    },
    { enabled },
  );
  const emailsQ = useGetList<EmailMessage>(
    "email_messages",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "date", order: "DESC" },
      filter: filterParams,
    },
    { enabled },
  );
  const recsQ = useGetList<ContactRecording>(
    "contact_recordings",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "DESC" },
      filter: filterParams,
    },
    { enabled },
  );
  const apptQ = useGetList<Appointment>(
    "appointments",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "start_at", order: "DESC" },
      filter: filterParams,
    },
    { enabled },
  );

  const isLoading =
    notesQ.isPending || emailsQ.isPending || recsQ.isPending || apptQ.isPending;

  const items: TimelineItem[] = useMemo(() => {
    const out: TimelineItem[] = [];

    (notesQ.data ?? []).forEach((n) => {
      out.push({
        key: `note-${n.id}`,
        type: "note",
        date: n.date,
        body: n.text,
      });
    });

    (emailsQ.data ?? []).forEach((m) => {
      const isOut = (m.folder ?? "").toUpperCase() !== "INBOX";
      out.push({
        key: `email-${m.id}`,
        type: isOut ? "email_out" : "email_in",
        date: m.date,
        subject: m.subject ?? "",
        body: m.text_body ?? "",
        from: m.from_name || m.from_email,
        to: m.to_emails?.map((t) => t.name || t.email).join(", "),
      });
    });

    (recsQ.data ?? []).forEach((r) => {
      out.push({
        key: `rec-${r.id}`,
        type: "appel",
        date: r.created_at,
        body: r.transcription ?? r.summary ?? "",
        duration: durationLabel(r.duration_seconds ?? 0),
      });
    });

    (apptQ.data ?? []).forEach((a) => {
      out.push({
        key: `appt-${a.id}`,
        type: "rdv",
        date: a.start_at,
        title: a.title,
        body: a.description ?? "",
      });
    });

    out.sort((a, b) => +new Date(b.date) - +new Date(a.date));
    return out;
  }, [notesQ.data, emailsQ.data, recsQ.data, apptQ.data]);

  const activeFilter = FILTERS.find((f) => f.key === filter)!;
  const filtered = items.filter((it) => activeFilter.matches.includes(it.type));

  const counts: Record<FilterKey, number> = {
    tous: items.length,
    mails: items.filter((i) => i.type === "email_in" || i.type === "email_out")
      .length,
    notes: items.filter((i) => i.type === "note").length,
    appels: items.filter((i) => i.type === "appel").length,
    rdv: items.filter((i) => i.type === "rdv").length,
  };

  return (
    <>
      {record && (
        <>
          <NoteCreateSheet
            open={noteOpen}
            onOpenChange={setNoteOpen}
            contact_id={record.id}
          />
          <TaskCreateSheet
            open={taskOpen}
            onOpenChange={setTaskOpen}
            contact_id={record.id}
          />
          <AudioRecorderDialog
            open={audioOpen}
            onOpenChange={setAudioOpen}
            contactId={Number(record.id)}
            contactName={`${record.first_name ?? ""} ${record.last_name ?? ""}`.trim()}
          />
        </>
      )}

      <Card>
        <CardContent className="py-4">
          {/* Toolbar — Sprint 7 T7.4.b : pills conformes maquette
              timeline.jsx (.filter-pill : transparent / hover light /
              active bg zinc-900 white). */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="flex flex-wrap gap-1">
              {FILTERS.map((f) => {
                const isActive = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={cn(
                      "h-7 px-2.5 rounded-md text-[12.5px] inline-flex items-center gap-1.5",
                      isActive
                        ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                    )}
                  >
                    {f.label}
                    {counts[f.key] > 0 && (
                      <span
                        className={cn(
                          "text-[10.5px] tabular-nums",
                          isActive ? "text-white/70" : "text-muted-foreground",
                        )}
                      >
                        {counts[f.key]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" size="sm" className="h-8 gap-1.5">
                    <Plus className="size-3.5" />
                    Ajouter
                    <ChevronDown className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setNoteOpen(true)}>
                    <StickyNote className="size-3.5" />
                    Note
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTaskOpen(true)}>
                    <CheckSquare className="size-3.5" />
                    Tâche
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAudioOpen(true)}>
                    <Phone className="size-3.5" />
                    Enregistrement audio
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Body */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="flex gap-3 items-start">
                  <Skeleton className="size-7 rounded-md shrink-0" />
                  <Skeleton className="h-3 w-20 mt-1" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-3/5" />
                    <Skeleton className="h-3 w-4/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="size-11 rounded-xl bg-muted text-muted-foreground flex items-center justify-center mb-3">
                <Inbox className="size-5" />
              </div>
              <p className="text-[14px] font-medium text-foreground mb-1">
                {filter === "tous"
                  ? "Aucune interaction enregistrée"
                  : `Aucun item dans "${activeFilter.label}"`}
              </p>
              <p className="text-[12.5px] text-muted-foreground mb-4 max-w-md">
                {filter === "tous"
                  ? "Ajoute une note, planifie une tâche ou attends qu'un email arrive pour commencer la timeline."
                  : "Change de filtre ou ajoute un nouvel élément."}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  className="gap-1.5"
                  onClick={() => setNoteOpen(true)}
                >
                  <Plus className="size-3.5" />
                  Ajouter une note
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setTaskOpen(true)}
                >
                  <CheckSquare className="size-3.5" />
                  Planifier une tâche
                </Button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((it) => {
                const Ic = ICONS[it.type];
                const isOpen = !!expanded[it.key];
                const hasFullBody = !!it.body && it.body.length > 220;
                return (
                  <div
                    key={it.key}
                    className="grid grid-cols-[28px_88px_1fr] gap-3 items-start py-3 cursor-pointer hover:bg-muted/30 -mx-1 px-1 rounded-sm transition-colors"
                    onClick={() =>
                      setExpanded((e) => ({ ...e, [it.key]: !e[it.key] }))
                    }
                  >
                    <div
                      className={cn(
                        "size-7 rounded-md flex items-center justify-center shrink-0",
                        ICON_BG[it.type],
                      )}
                    >
                      <Ic className="size-3.5" />
                    </div>
                    <div className="flex flex-col text-[11.5px] leading-tight pt-0.5">
                      <span className="text-foreground">
                        {relativeDate(it.date)}
                      </span>
                      <span className="text-muted-foreground">
                        {absoluteDate(it.date)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12.5px]">
                        <span className="font-medium text-foreground">
                          {TYPE_LABEL[it.type]}
                        </span>
                        {it.from && (
                          <span className="text-muted-foreground">
                            de {it.from}
                          </span>
                        )}
                        {it.to && (
                          <span className="text-muted-foreground">
                            à {it.to}
                          </span>
                        )}
                        {it.duration && (
                          <span className="text-muted-foreground">
                            · {it.duration}
                          </span>
                        )}
                      </div>
                      {it.title && (
                        <div className="text-[13px] font-medium text-foreground mt-0.5">
                          {it.title}
                        </div>
                      )}
                      {it.subject && (
                        <div className="text-[13px] font-medium text-foreground mt-0.5">
                          {it.subject}
                        </div>
                      )}
                      {it.body && (
                        <div className="text-[12.5px] text-muted-foreground mt-1 whitespace-pre-line">
                          {isOpen ? it.body : snippet(it.body)}
                        </div>
                      )}
                      {hasFullBody && (
                        <button
                          type="button"
                          className="text-[11.5px] text-primary hover:underline mt-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpanded((ex) => ({
                              ...ex,
                              [it.key]: !ex[it.key],
                            }));
                          }}
                        >
                          {isOpen ? "Voir moins" : "Voir plus"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
